import { FC, useState } from "react";

import { Alert, Button, Form, Input, notification, Select } from "antd";
import Title from "antd/lib/typography/Title";
import { useForm } from "antd/lib/form/Form";

import { UsersApi } from "@/api";
import { ERROR_500_MESSAGE } from "@/api/constants";
import { IUser } from "@/models/User";
import { useAppSelector } from "@/hooks/redux";

interface CreateUserProps {
  onCreated?: (user: IUser) => void;
}

const { Option } = Select;

const CreateUser: FC<CreateUserProps> = ({ onCreated }) => {
   const areUsers = useAppSelector((state) => state.userReducer.areUsers);
  const [form] = useForm();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleFinish = async (values:any) => {
    try {
      setLoading(true);
      setError("");
      const user = await UsersApi.create({
        userType: areUsers ? values.userType : "admin",
        login: values.login,
        password: values.password,
      });
      notification.open({
        type: "success",
        message: "User created",
      });
      form.resetFields();
      onCreated && onCreated(user);
    } catch (e: any) {
      if (e.response?.data?.message) {
        setError(e.response?.data?.message);
      } else {
        setError(ERROR_500_MESSAGE);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Form form={form} requiredMark={false} name="login" onFinish={handleFinish}>
      <Form.Item>
        <Title level={4}>Create user</Title>
      </Form.Item>

      {areUsers && (
        <Form.Item
          label="User type"
          name="userType"
          rules={[{ required: true, message: "Please select user type" }]}
        >
          <Select placeholder="Select an option" allowClear>
            <Option key="operator" value="operator">
              operator
            </Option>
            <Option key="administrator" value="admin">
              administrator
            </Option>
          </Select>
        </Form.Item>
      )}

      <Form.Item
        label="Login"
        name="login"
        rules={[{ required: true, message: "Please input the login" }]}
      >
        <Input />
      </Form.Item>

      <Form.Item
        label="Password"
        name="password"
        rules={[{ required: true, message: "Please input the password" }]}
      >
        <Input.Password />
      </Form.Item>

      <Form.Item
        label="Confirm password"
        name="confirmPassword"
        rules={[
          { required: true, message: "Please input the password" },
          ({ getFieldValue }) => ({
            validator(_, value) {
              if (!value || getFieldValue("password") === value) {
                return Promise.resolve();
              }
              return Promise.reject(new Error("Passwords do not match"));
            },
          }),
        ]}
      >
        <Input.Password />
      </Form.Item>

      {!!error && (
        <Form.Item>
          <Alert message={error} type="error" />
        </Form.Item>
      )}

      <Form.Item>
        <Button
          disabled={loading}
          loading={loading}
          type="primary"
          htmlType="submit"
        >
          Create
        </Button>
      </Form.Item>
    </Form>
  );
};

export default CreateUser;
